import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { beneficiaryAPI, departmentAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BookUser, Plus, Loader2, Search, Trash2, Ban, CheckCircle, Building2, CreditCard, Smartphone, UserCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBeneficiaries = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    department_id: '',
    name: '',
    beneficiary_type: 'UPI',
    upi_id: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: '',
    nickname: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [benefRes, deptRes] = await Promise.all([
        fetch(`${API_URL}/api/beneficiaries`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        departmentAPI.list()
      ]);
      
      if (benefRes.ok) {
        const benefData = await benefRes.json();
        setBeneficiaries(benefData);
      }
      setDepartments(deptRes.data);
    } catch (err) {
      toast.error('Failed to load beneficiaries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.department_id || !formData.name) {
      toast.error('Department and name are required');
      return;
    }
    
    if (formData.beneficiary_type === 'UPI' && !formData.upi_id) {
      toast.error('UPI ID is required');
      return;
    }
    
    if (formData.beneficiary_type === 'BANK_ACCOUNT' && (!formData.account_number || !formData.ifsc_code)) {
      toast.error('Account number and IFSC are required');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/beneficiaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Beneficiary added successfully');
        setIsCreateOpen(false);
        setFormData({
          department_id: '',
          name: '',
          beneficiary_type: 'UPI',
          upi_id: '',
          account_number: '',
          ifsc_code: '',
          account_holder_name: '',
          nickname: '',
          notes: ''
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add beneficiary');
      }
    } catch (err) {
      toast.error('Failed to add beneficiary');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (beneficiaryId, name) => {
    if (!window.confirm(`Delete beneficiary "${name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/beneficiaries/${beneficiaryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Beneficiary deleted');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to delete beneficiary');
    }
  };

  const handleToggleBlacklist = async (beneficiaryId, currentStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/beneficiaries/${beneficiaryId}/blacklist`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(currentStatus ? 'Beneficiary removed from blacklist' : 'Beneficiary blacklisted');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to update blacklist status');
    }
  };

  const getDeptName = (deptId) => {
    const dept = departments.find(d => d.department_id === deptId);
    return dept ? dept.name : deptId;
  };

  const filteredBeneficiaries = beneficiaries.filter(b => {
    const matchesSearch = b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.upi_id && b.upi_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (b.account_number && b.account_number.includes(searchTerm));
    const matchesDept = filterDept === 'all' || b.department_id === filterDept;
    return matchesSearch && matchesDept;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-beneficiaries-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Beneficiaries</h1>
            <p className="text-gray-500 mt-1">Manage payment recipients across all departments</p>
          </div>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="add-beneficiary-btn">
                <Plus className="w-4 h-4" /> Add Beneficiary
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900 flex items-center gap-2">
                  <BookUser className="w-5 h-5 text-blue-600" />
                  Add New Beneficiary
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Department Selection */}
                <div className="space-y-2">
                  <Label className="text-gray-600">Department *</Label>
                  <Select value={formData.department_id} onValueChange={(val) => setFormData({...formData, department_id: val})}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="dept-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300">
                      {departments.map(d => (
                        <SelectItem key={d.department_id} value={d.department_id} className="text-gray-900">
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label className="text-gray-600">Beneficiary Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                    placeholder="John Doe" 
                    className="bg-white border-gray-300 text-gray-900"
                    data-testid="name-input"
                  />
                </div>

                {/* Payment Type */}
                <div className="space-y-2">
                  <Label className="text-gray-600">Payment Type *</Label>
                  <Tabs value={formData.beneficiary_type} onValueChange={(val) => setFormData({...formData, beneficiary_type: val})}>
                    <TabsList className="grid grid-cols-2 bg-gray-100">
                      <TabsTrigger value="UPI" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                        <Smartphone className="w-4 h-4 mr-2" /> UPI
                      </TabsTrigger>
                      <TabsTrigger value="BANK_ACCOUNT" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                        <CreditCard className="w-4 h-4 mr-2" /> Bank Account
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                {/* UPI Fields */}
                {formData.beneficiary_type === 'UPI' && (
                  <div className="space-y-2">
                    <Label className="text-gray-600">UPI ID *</Label>
                    <Input 
                      value={formData.upi_id} 
                      onChange={(e) => setFormData({...formData, upi_id: e.target.value})} 
                      placeholder="name@upi" 
                      className="bg-white border-gray-300 text-gray-900 font-mono"
                      data-testid="upi-input"
                    />
                  </div>
                )}

                {/* Bank Account Fields */}
                {formData.beneficiary_type === 'BANK_ACCOUNT' && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-gray-600">Account Number *</Label>
                      <Input 
                        value={formData.account_number} 
                        onChange={(e) => setFormData({...formData, account_number: e.target.value})} 
                        placeholder="1234567890" 
                        className="bg-white border-gray-300 text-gray-900 font-mono"
                        data-testid="account-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-600">IFSC Code *</Label>
                      <Input 
                        value={formData.ifsc_code} 
                        onChange={(e) => setFormData({...formData, ifsc_code: e.target.value.toUpperCase()})} 
                        placeholder="HDFC0001234" 
                        className="bg-white border-gray-300 text-gray-900 font-mono"
                        data-testid="ifsc-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-600">Account Holder Name</Label>
                      <Input 
                        value={formData.account_holder_name} 
                        onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})} 
                        placeholder="Name as per bank records" 
                        className="bg-white border-gray-300 text-gray-900"
                      />
                    </div>
                  </>
                )}

                {/* Optional Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-600">Nickname</Label>
                    <Input 
                      value={formData.nickname} 
                      onChange={(e) => setFormData({...formData, nickname: e.target.value})} 
                      placeholder="Optional" 
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600">Notes</Label>
                    <Input 
                      value={formData.notes} 
                      onChange={(e) => setFormData({...formData, notes: e.target.value})} 
                      placeholder="Optional" 
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="border-gray-300">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreate} 
                  className="bg-blue-600 hover:bg-blue-700 text-white" 
                  disabled={creating}
                  data-testid="save-beneficiary-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} 
                  Add Beneficiary
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              placeholder="Search by name, UPI, or account..." 
              className="pl-10 bg-white border-gray-300 text-gray-900"
              data-testid="search-input"
            />
          </div>
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-48 bg-white border-gray-300 text-gray-900">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-300">
              <SelectItem value="all" className="text-gray-900">All Departments</SelectItem>
              {departments.map(d => (
                <SelectItem key={d.department_id} value={d.department_id} className="text-gray-900">
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Beneficiaries Table */}
        <Card className="glass-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200">
                  <TableHead className="text-gray-600">Name</TableHead>
                  <TableHead className="text-gray-600">Department</TableHead>
                  <TableHead className="text-gray-600">Type</TableHead>
                  <TableHead className="text-gray-600">Details</TableHead>
                  <TableHead className="text-gray-600">Created By</TableHead>
                  <TableHead className="text-gray-600">Payments</TableHead>
                  <TableHead className="text-gray-600">Status</TableHead>
                  <TableHead className="text-gray-600 w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBeneficiaries.map((b) => (
                  <TableRow key={b.beneficiary_id} className="border-gray-200">
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{b.name}</p>
                        {b.nickname && <p className="text-xs text-gray-500">{b.nickname}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-blue-200 text-blue-700">
                        {b.department_name || getDeptName(b.department_id)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={b.beneficiary_type === 'UPI' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}>
                        {b.beneficiary_type === 'UPI' ? <Smartphone className="w-3 h-3 mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />}
                        {b.beneficiary_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-gray-600">
                      {b.upi_id || `${b.account_number} (${b.ifsc_code})`}
                    </TableCell>
                    <TableCell>
                      {b.created_by_name ? (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <UserCircle className="w-4 h-4 text-gray-400" />
                          <span>{b.created_by_name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="text-gray-900">{b.payment_count} payments</p>
                        <p className="text-gray-500">{formatCurrency(b.total_amount_sent)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {b.is_blacklisted ? (
                        <Badge className="bg-red-100 text-red-700">Blacklisted</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleToggleBlacklist(b.beneficiary_id, b.is_blacklisted)}
                          className={b.is_blacklisted ? "text-green-600 hover:bg-green-50" : "text-orange-600 hover:bg-orange-50"}
                          title={b.is_blacklisted ? "Remove from blacklist" : "Blacklist"}
                        >
                          {b.is_blacklisted ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(b.beneficiary_id, b.name)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredBeneficiaries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                      {searchTerm || filterDept !== 'all' ? 'No beneficiaries match your filters' : 'No beneficiaries added yet'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminBeneficiaries;
